import { z } from "zod";
import type { AssessmentChoice } from "./assessment-choice";
import type { CareMeetingRequiredReason } from "./care-meeting-requirement";
import type { JourneyStepsInput } from "./journey-steps";

// TES-1656 (Emelie, TES-1653 §4–6, 2026-11-02) — a new-treatment patient's
// progress before the doctor's decision, as four main steps:
//
//   > "1. Din första kartläggning. 2. Din hälsodeklaration. 3. Underlag inför
//   > bedömningen. 4. Läkarens bedömning och besked."
//   > "Samma huvudsteg ska användas i Hem-toppen och i stegöversikten. Under
//   > respektive huvudsteg visas relevanta deluppgifter och deras status."
//
// The rules this derivation keeps:
//   - A sub-step is listed only when it applies to her ("bilder, midjemått
//     och blodprov … bockas av separat när de behövs"). Nothing is ever
//     "Ej aktuellt".
//   - "Visa inte 'Boka vårdmöte' och 'Vårdmöte' med 'Ej aktuellt'." The
//     meeting appears only in her actual path: chosen by her, required by the
//     doctor or by her declaration's flags, booked, or (with the tenant's
//     optional meeting off) the meeting-first journey. A required meeting is
//     told apart from a chosen one ("Skilj patientvalt möte från nödvändigt,
//     avgiftsfritt möte").
//   - The steps end at the doctor's decision ("progressen beskriver vägen
//     till besked, inte lova behandlingsstart"). Activation and start follow
//     a positive decision and are not counted here.
//
// TES-1660 (Emelie, Ö-1043, 2026-11-02): a prescription transfer uses the
// same four main steps, with her own sub-steps:
//
//   > "Under step 3 (Underlag inför bedömningen): uppgifter om pågående
//   > behandling, recept eller ordinationsunderlag, övriga medicinska
//   > underlag, eventuell riktad komplettering."
//   > "Under step 4 (Läkarens bedömning och besked): medicinsk granskning,
//   > eventuell komplettering som läkaren begär under granskningen, beslut
//   > om receptövertag."
//   > "Fortsatt behandling ligger efter onboardingprogressen och ska inte
//   > vara ett femte huvudsteg."
//
// What there is to show: F-07 carries the treatment details and the
// prescription upload, and every komplettering (F-07 sections, CP-06 tasks,
// a new blood test) is asked for by the doctor once the review has begun.
// So step 3 lists F-07's two parts, and the doctor's komplettering is one
// row under step 4, listed only once asked for. F-07 has asked for no other
// medical underlag since v4 (TES-761), so that row has nothing to stand for
// yet. Both readings are open as Ö-1056. A meeting the doctor requires
// (meeting_required, fee-free since TES-762) is in her path, as on the
// new-treatment track. Served only to builds at
// CLIENT_CONTRACT_CHANGES.transferMainSteps.
//
// Served as /me `mainSteps` to a build at CLIENT_CONTRACT_CHANGES.
// fourMainSteps, which renders it both as the Hem top's count and as the
// step overview. Every older build keeps the six-milestone top and the
// Dina steg ladder. The copy is the server's, so Emelie's sub-step wording
// (still being finished) lands without a release.

export const MAIN_STEP_KEYS = [
  "first_mapping",
  "health_declaration",
  "assessment_evidence",
  "doctor_decision",
] as const;
export const mainStepKeySchema = z.enum(MAIN_STEP_KEYS);
export type MainStepKey = z.infer<typeof mainStepKeySchema>;

export const MAIN_SUB_STEP_KEYS = [
  // Under "Din hälsodeklaration": the choice of assessment after F-02, on
  // the optional meeting track (TES-1646, Ö-1015).
  "assessment_choice",
  // Under "Underlag inför bedömningen".
  "photos",
  "waist",
  "labs",
  // Under "Läkarens bedömning och besked", only when a meeting is in her path.
  "book_meeting",
  "meeting",
  // TES-1660 (Ö-1043): a prescription transfer's own sub-steps. Under
  // "Underlag inför bedömningen": F-07's treatment details and its
  // prescription or order. Under "Läkarens bedömning och besked": the
  // review, a komplettering once the doctor asks for one, and the decision.
  "transfer_treatment",
  "transfer_prescription",
  "transfer_review",
  "transfer_completion",
  "transfer_decision",
] as const;
export const mainSubStepKeySchema = z.enum(MAIN_SUB_STEP_KEYS);
export type MainSubStepKey = z.infer<typeof mainSubStepKeySchema>;

// Why a meeting is in her path. `required` is the free meeting the doctor
// or her declaration's flags asked for (careMeetingRequiredReason); `chosen`
// is the meeting she chose or booked herself on the optional track;
// `standard` is the meeting-first journey (the tenant's optional meeting
// off, or an F-01 outcome that keeps the meeting).
export const MAIN_STEP_MEETING_KINDS = [
  "chosen",
  "required",
  "standard",
] as const;
export const mainStepMeetingKindSchema = z.enum(MAIN_STEP_MEETING_KINDS);
export type MainStepMeetingKind = z.infer<typeof mainStepMeetingKindSchema>;

export const mainSubStepSchema = z.object({
  key: mainSubStepKeySchema,
  status: z.enum(["done", "active", "upcoming"]),
  title: z.string(),
  // One line under the title, e.g. "Mötet kostar inget." on a required meeting.
  note: z.string().optional(),
  // book_meeting and meeting only.
  meetingKind: mainStepMeetingKindSchema.optional(),
});
export type MainSubStep = z.infer<typeof mainSubStepSchema>;

export const mainStepSchema = z.object({
  key: mainStepKeySchema,
  // `attention`: her health declaration stopped (the ladder's intake
  // attention), something she has to act on.
  status: z.enum(["done", "active", "upcoming", "attention"]),
  title: z.string(),
  subSteps: z.array(mainSubStepSchema),
});
export type MainStep = z.infer<typeof mainStepSchema>;

export const MAIN_STEPS_TOTAL = 4;

export const mainStepsSchema = z.object({
  // "X av 4 steg klara": the number of main steps done.
  completed: z.number().int().min(0).max(MAIN_STEPS_TOTAL),
  total: z.literal(MAIN_STEPS_TOTAL),
  steps: z.array(mainStepSchema).length(MAIN_STEPS_TOTAL),
});
export type MainSteps = z.infer<typeof mainStepsSchema>;

type Locale = "sv" | "en";

// The four titles are Emelie's (TES-1653 §5), verbatim. The sub-step titles
// are the existing task titles (Hem's cards "Lämna dina bilder" and "Mät
// ditt midjemått", Dina steg's "Boka vårdmöte" and "Vårdmöte", her own
// "blodprov") until her sub-step copy is done. "Välj bedömningssätt" has no
// existing title and is a proposal (TES-475). The English is ours.
// TES-1660: the transfer sub-steps are Ö-1043's words, capitalised.
// "Uppgifter om din pågående behandling" is F-07's own name in Drive, and
// "Komplettering" is a proposal (Ö-1057).
export const MAIN_STEPS_COPY: Record<
  Locale,
  {
    steps: Record<MainStepKey, string>;
    subSteps: Record<MainSubStepKey, string>;
    requiredMeetingNote: string;
  }
> = {
  sv: {
    steps: {
      first_mapping: "Din första kartläggning",
      health_declaration: "Din hälsodeklaration",
      assessment_evidence: "Underlag inför bedömningen",
      doctor_decision: "Läkarens bedömning och besked",
    },
    subSteps: {
      assessment_choice: "Välj bedömningssätt",
      photos: "Lämna dina bilder",
      waist: "Mät ditt midjemått",
      labs: "Blodprov",
      book_meeting: "Boka vårdmöte",
      meeting: "Vårdmöte",
      transfer_treatment: "Uppgifter om din pågående behandling",
      transfer_prescription: "Recept eller ordinationsunderlag",
      transfer_review: "Medicinsk granskning",
      transfer_completion: "Komplettering",
      transfer_decision: "Beslut om receptövertag",
    },
    // The required meeting card's own line (CARE_MEETING_REQUIRED_CARD).
    requiredMeetingNote: "Mötet kostar inget.",
  },
  en: {
    steps: {
      first_mapping: "Your first mapping",
      health_declaration: "Your health declaration",
      assessment_evidence: "Information for the assessment",
      doctor_decision: "The doctor's assessment and decision",
    },
    subSteps: {
      assessment_choice: "Choose how you are assessed",
      photos: "Submit your photos",
      waist: "Measure your waist",
      labs: "Blood test",
      book_meeting: "Book a video consultation",
      meeting: "Consultation",
      transfer_treatment: "Details of your ongoing treatment",
      transfer_prescription: "Prescription or order",
      transfer_review: "Medical review",
      transfer_completion: "Additional information",
      transfer_decision: "Decision on the prescription transfer",
    },
    requiredMeetingNote: "The visit is free of charge.",
  },
};

/**
 * The facts behind the four steps: the Dina steg ladder's own signals (read
 * once on /me, so the two can never disagree) and the optional meeting
 * track's.
 */
export interface MainStepsInput extends Pick<
  JourneyStepsInput,
  | "f01Done"
  | "hasIntake"
  | "intakeClean"
  | "acuteSafetyActive"
  | "consultBooked"
  | "consultHeld"
  | "labsOrdered"
  | "labsAwaitingPayment"
  | "careFeedbackReceived"
  | "medicalEvidenceWaiting"
  | "publishedDecision"
> {
  /**
   * The tenant's optional meeting applies to her (flag, group and a positive
   * F-01: onOptionalCareMeetingTrack), whether or not a meeting has since
   * been required. False: the meeting-first journey.
   */
  optionalMeetingTrack: boolean;
  /** Why a meeting is required of her; null when none is. */
  meetingRequiredReason: CareMeetingRequiredReason | null;
  /** Her stored choice of assessment (TES-1646); null before she chose. */
  assessmentChoice: AssessmentChoice | null;
  /** The written track's photo task: null when it is not hers. */
  photos: "open" | "submitted" | null;
  /** The waist task (TES-1649): null when it is not required of her. */
  waist: "open" | "submitted" | null;
  /**
   * TES-1660: which ladder she is on. PRESCRIPTION_TRANSFER derives the
   * transfer's sub-steps from `transferReview`; absent means new treatment.
   */
  patientGroup?: JourneyStepsInput["patientGroup"];
  /** TES-1660: the transfer review row; null until F-02 and F-07 are in. */
  transferReview?: JourneyStepsInput["transferReview"];
}

type SubStatus = MainSubStep["status"];

function meetingKindOf(s: MainStepsInput): MainStepMeetingKind | null {
  if (!s.optionalMeetingTrack) return "standard";
  if (s.meetingRequiredReason !== null) return "required";
  if (s.assessmentChoice === "meeting" || s.consultBooked || s.consultHeld) {
    return "chosen";
  }
  return null;
}

export function deriveMainSteps(
  s: MainStepsInput,
  locale: Locale = "sv",
): MainSteps {
  const copy = MAIN_STEPS_COPY[locale] ?? MAIN_STEPS_COPY.sv;
  if (s.patientGroup === "PRESCRIPTION_TRANSFER") {
    return transferMainSteps(s, copy);
  }
  const decided = s.publishedDecision !== "none";
  const booked = s.consultBooked || s.consultHeld;
  const kind = meetingKindOf(s);

  // ---- 1. "Din första kartläggning"
  const mappingDone = s.f01Done || s.hasIntake || booked || decided;

  // ---- 2. "Din hälsodeklaration" (+ the choice of assessment)
  // TES-675 — an acute stop reads as done: the acute path owns it.
  const declarationDone = s.hasIntake && (s.intakeClean || s.acuteSafetyActive);
  const declarationAttention =
    s.hasIntake && !s.intakeClean && !s.acuteSafetyActive;
  // The choice is hers only on the optional track with no meeting required.
  const choiceApplies =
    s.optionalMeetingTrack && s.meetingRequiredReason === null;
  const choiceDone = s.assessmentChoice !== null || booked || decided;
  const choiceStatus: SubStatus = choiceDone
    ? "done"
    : declarationDone && !s.acuteSafetyActive
      ? "active"
      : "upcoming";
  const declarationStepDone =
    decided || (declarationDone && (!choiceApplies || choiceDone));

  // ---- 3. "Underlag inför bedömningen"
  const evidenceOpen = declarationStepDone && !decided;
  const evidenceSubs: MainSubStep[] = [];
  const task = (
    key: MainSubStepKey,
    done: boolean,
    canStart: boolean,
  ): void => {
    // After the decision a task she never did is no longer part of her
    // path: it is left out rather than ticked.
    if (decided && !done) return;
    evidenceSubs.push({
      key,
      status: done ? "done" : canStart ? "active" : "upcoming",
      title: copy.subSteps[key],
    });
  };
  if (s.photos !== null) {
    task("photos", s.photos === "submitted", evidenceOpen);
  }
  if (s.waist !== null) {
    task("waist", s.waist === "submitted", evidenceOpen);
  }
  // The blood test, once the doctor has asked for one (the ladder's
  // `labsOrdered`, or the CP-06 test still to be paid). Done when her CP-06
  // tasks are done, the doctor has answered the result, or decided.
  if (s.labsOrdered || s.labsAwaitingPayment === true) {
    task(
      "labs",
      s.medicalEvidenceWaiting || s.careFeedbackReceived,
      evidenceOpen,
    );
  }
  // On a meeting path the doctor asks for evidence in the meeting, so the
  // step cannot be complete before it is held.
  const meetingSettled = kind === null || s.consultHeld;
  const evidenceDone =
    decided ||
    (declarationStepDone &&
      meetingSettled &&
      evidenceSubs.every((sub) => sub.status === "done"));

  // ---- 4. "Läkarens bedömning och besked"
  const decisionSubs: MainSubStep[] = [];
  if (kind !== null) {
    // When she can book: the meeting-first journey right after F-01 (the
    // booking comes before the declaration there), a required meeting at
    // once, a chosen one once she has chosen it.
    const canBook =
      kind === "standard"
        ? mappingDone
        : kind === "required" || s.assessmentChoice === "meeting";
    const note = kind === "required" ? { note: copy.requiredMeetingNote } : {};
    const meetingSub = (
      key: "book_meeting" | "meeting",
      done: boolean,
      active: boolean,
    ): void => {
      if (decided && !done) return;
      decisionSubs.push({
        key,
        status: done ? "done" : active ? "active" : "upcoming",
        title: copy.subSteps[key],
        ...note,
        meetingKind: kind,
      });
    };
    meetingSub(
      "book_meeting",
      booked,
      canBook && !s.acuteSafetyActive && !decided,
    );
    meetingSub("meeting", s.consultHeld, s.consultBooked && !s.consultHeld);
  }

  const done: Record<MainStepKey, boolean> = {
    first_mapping: mappingDone,
    health_declaration: declarationStepDone,
    assessment_evidence: evidenceDone,
    doctor_decision: decided,
  };
  const subs: Record<MainStepKey, MainSubStep[]> = {
    first_mapping: [],
    health_declaration: choiceApplies
      ? [
          {
            key: "assessment_choice",
            status: choiceStatus,
            title: copy.subSteps.assessment_choice,
          },
        ]
      : [],
    assessment_evidence: evidenceSubs,
    doctor_decision: decisionSubs,
  };
  // Whether the step itself, apart from its sub-steps, waits on her or on
  // the doctor now: F-01, the declaration, and the doctor's turn once the
  // first three are done.
  const ownTurn: Record<MainStepKey, boolean> = {
    first_mapping: !mappingDone,
    health_declaration: mappingDone && !declarationDone,
    assessment_evidence: false,
    doctor_decision:
      mappingDone && declarationStepDone && evidenceDone && !decided,
  };

  return assemble(copy, done, subs, ownTurn, declarationAttention);
}

type Copy = (typeof MAIN_STEPS_COPY)[Locale];

function assemble(
  copy: Copy,
  done: Record<MainStepKey, boolean>,
  subs: Record<MainStepKey, MainSubStep[]>,
  ownTurn: Record<MainStepKey, boolean>,
  declarationAttention: boolean,
): MainSteps {
  const steps = MAIN_STEP_KEYS.map((key): MainStep => {
    const status: MainStep["status"] = done[key]
      ? "done"
      : key === "health_declaration" && declarationAttention
        ? "attention"
        : ownTurn[key] || subs[key].some((sub) => sub.status === "active")
          ? "active"
          : "upcoming";
    return { key, status, title: copy.steps[key], subSteps: subs[key] };
  });

  return {
    completed: steps.filter((step) => step.status === "done").length,
    total: MAIN_STEPS_TOTAL,
    steps,
  };
}

// TES-1660: the prescription transfer's four steps. The review row is the
// one source of where the case is (prescription-transfer-review.ts), as on
// the transfer ladder: it exists once F-02 and F-07 are both in.
function transferMainSteps(s: MainStepsInput, copy: Copy): MainSteps {
  const r = s.transferReview ?? null;
  const status = r?.status ?? null;
  // A refusal is a decision too: the steps lead to her besked, either way.
  const decided = status === "approved" || status === "cannot_transfer";
  const sub = (
    key: MainSubStepKey,
    subStatus: SubStatus,
    extra: Partial<MainSubStep> = {},
  ): MainSubStep => ({
    key,
    status: subStatus,
    title: copy.subSteps[key],
    ...extra,
  });

  // ---- 1. "Din första kartläggning": F-01 put her on this track.
  const mappingDone = s.f01Done || s.hasIntake || r !== null;

  // ---- 2. "Din hälsodeklaration" (F-02). TES-675: an acute stop reads as
  // done, the acute path owns it. No assessment choice: "Receptövertag och
  // obligatorisk mötesväg får inte det fria valet" (work list, punkt 5).
  const declarationDone =
    r !== null || (s.hasIntake && (s.intakeClean || s.acuteSafetyActive));
  const declarationAttention =
    r === null && s.hasIntake && !s.intakeClean && !s.acuteSafetyActive;

  // ---- 3. "Underlag inför bedömningen": F-07, sent in as one form.
  const f07Done = r !== null;
  const f07Status: SubStatus = f07Done
    ? "done"
    : declarationDone && !s.acuteSafetyActive
      ? "active"
      : "upcoming";
  const evidenceSubs = [
    sub("transfer_treatment", f07Status),
    sub("transfer_prescription", f07Status),
  ];

  // ---- 4. "Läkarens bedömning och besked"
  const decisionSubs: MainSubStep[] = [
    sub(
      "transfer_review",
      decided ? "done" : r !== null ? "active" : "upcoming",
    ),
  ];
  // A meeting the doctor requires before deciding (TES-762): fee-free and
  // never her choice. Left out after the decision if it never took place.
  if (status === "meeting_required" || s.consultBooked || s.consultHeld) {
    const booked = s.consultBooked || s.consultHeld;
    const meetingSub = (
      key: "book_meeting" | "meeting",
      subDone: boolean,
      active: boolean,
    ): void => {
      if (decided && !subDone) return;
      decisionSubs.push(
        sub(key, subDone ? "done" : active ? "active" : "upcoming", {
          note: copy.requiredMeetingNote,
          meetingKind: "required",
        }),
      );
    };
    meetingSub(
      "book_meeting",
      booked,
      status === "meeting_required" && !s.acuteSafetyActive,
    );
    meetingSub("meeting", s.consultHeld, s.consultBooked && !s.consultHeld);
  }
  // The doctor's komplettering: F-07 sections or CP-06 tasks
  // (completion_requested), or a new blood test (labs_required, TES-734).
  // Listed once asked for, and kept as done after she has sent it in.
  const completing =
    status === "completion_requested" || status === "labs_required";
  if (
    completing ||
    r?.completionEverRequested === true ||
    (r !== null && (s.labsOrdered || s.labsAwaitingPayment === true))
  ) {
    // Her part is done once her CP-06 tasks are all in and the case waits
    // on the result or the doctor (medicalEvidenceWaiting).
    decisionSubs.push(
      sub(
        "transfer_completion",
        completing && !s.medicalEvidenceWaiting ? "active" : "done",
      ),
    );
  }
  decisionSubs.push(sub("transfer_decision", decided ? "done" : "upcoming"));

  return assemble(
    copy,
    {
      first_mapping: mappingDone,
      health_declaration: declarationDone,
      assessment_evidence: f07Done,
      doctor_decision: decided,
    },
    {
      first_mapping: [],
      health_declaration: [],
      assessment_evidence: evidenceSubs,
      doctor_decision: decisionSubs,
    },
    {
      first_mapping: !mappingDone,
      health_declaration: mappingDone && !declarationDone,
      assessment_evidence: false,
      doctor_decision: f07Done && !decided,
    },
    declarationAttention,
  );
}
